import React, { useEffect, useState } from 'react';
import type { TProviderRecentlyWorked } from '../../home/new/types';
import { Avatar, Colors, Link, LoadingIndicator, SmallIcon, Text, TextArea } from 'library';
import DefaultProfileImage from 'assets/icons/Profile.svg';
import { Grid, Rating, styled } from '@mui/material';
import { IProviderReviewOption, ProviderReviewSubmitResponse } from 'models';
import { Controller, useForm } from 'react-hook-form';
import ReviewOptionSelect from './ReviewOptionsSelect';
import { useClient } from 'shared';
import { Check } from '@mui/icons-material';

interface ReviewFormValues {
    text?: string;
    options?: number[];
    score?: number;
    needs_improvement?: number[];
    going_well?: number[];
}

export default function ProviderReviewModalLine({
    worker,
    options,
    businessLocationId,
}: {
    worker: TProviderRecentlyWorked;
    options: IProviderReviewOption[];
    businessLocationId?: number;
}) {
    const [isOpen, setOpen] = useState(false);
    const { send, loading, data, error } = useClient<ProviderReviewSubmitResponse>({
        url: 'api/provider-review/',
        options: { sendImmediately: false },
    });
    const {
        control,
        formState: { isDirty },
        handleSubmit,
        watch,
        reset,
    } = useForm<ReviewFormValues>();

    const totalScore = watch('score');
    const selectedOptions = watch(['options', 'going_well', 'needs_improvement']);

    const positiveOptions = options.filter((x) => x.is_positive);
    const negativeOptions = options.filter((x) => x.is_negative);
    const otherOptions = options.filter((x) => !x.is_positive && !x.is_negative);

    const onSubmit = (values: ReviewFormValues) => {
        const optionsSelected = [
            ...(values.options ?? []),
            ...(values.going_well ?? []),
            ...(values.needs_improvement ?? []),
        ];

        send({
            body: {
                reviewee: worker.id,
                total_score: values.score,
                text: values.text,
                review_results: options
                    .filter((option) => optionsSelected?.includes(option.id))
                    .map((it) => ({
                        option: it.id,
                    })),
                business_location: businessLocationId,
            },
        }).then(() => {
            setOpen(false);
        });
    };

    useEffect(() => {
        reset({
            text: data?.text,
            score: data?.total_score,
            options: options
                .filter(
                    (it) => !it.is_negative && !it.is_positive && data?.review_results?.some((i) => i.option === it.id),
                )
                .map((it) => it.id),
            going_well: options
                .filter((it) => it.is_positive && data?.review_results?.some((i) => i.option === it.id))
                .map((it) => it.id),
            needs_improvement: options
                .filter((it) => it.is_negative && data?.review_results?.some((i) => i.option === it.id))
                .map((it) => it.id),
        });
    }, [data, reset]);

    const buttonDetails = (() => {
        if (!isOpen) {
            return {
                text: data ? 'Edit' : 'Review',
                action: () => setOpen(true),
            };
        }
        if (isDirty) {
            return {
                text: 'Submit',
                action: totalScore && selectedOptions?.length ? handleSubmit(onSubmit) : () => {},
                disabled: !totalScore || !selectedOptions?.length,
            };
        }

        return {
            text: 'Close',
            action: () => setOpen(false),
        };
    })();

    return (
        <Grid key={worker.id} container gap={3}>
            <Grid container item gap={2} direction="row" alignItems="center" wrap="nowrap">
                <Grid item>
                    {worker.profile_image?.image ? (
                        <Avatar src={worker.profile_image?.image} style={{ height: 70, width: 70 }} />
                    ) : (
                        <DefaultProfileImage style={{ height: 70, width: 70 }} />
                    )}
                </Grid>
                <Grid container item>
                    <Text variant="h1">
                        {worker.user.first_name} {worker.user.last_name}
                    </Text>
                </Grid>
                <Grid item>
                    {!!data && (
                        <Grid container direction="row" gap={1} flexWrap="nowrap">
                            <Text variant="h2" bold>
                                Reviewed
                            </Text>
                            <SmallIcon icon={<Check />} backgroundColor={Colors.turquoise} color={Colors.white} />
                        </Grid>
                    )}
                    {loading ? (
                        <LoadingIndicator />
                    ) : (
                        <Link onClick={buttonDetails.action}>
                            <Text
                                bold
                                variant="h2"
                                textStyle={{ color: !buttonDetails.disabled ? Colors.darkNavy : Colors.lightGrey }}
                            >
                                {buttonDetails.text}
                            </Text>
                        </Link>
                    )}
                </Grid>
                {!!error && (
                    <Text color={Colors.error}>
                        There was an error submitting your review. Please refresh your page and try again.
                    </Text>
                )}
            </Grid>
            {isOpen && (
                <Grid gap={2} container direction="column">
                    <Grid>
                        <Text bold>Provide a star rating</Text>
                        <Controller
                            control={control}
                            name="score"
                            render={({ field }) => (
                                <Rating
                                    size="large"
                                    value={field.value ?? 0}
                                    name="Overall"
                                    onChange={(_, value) => field.onChange(value)}
                                />
                            )}
                        />
                    </Grid>
                    {totalScore && (
                        <>
                            <Grid>
                                <Text variant="caption" bold>
                                    Select at least one to continue
                                </Text>
                            </Grid>
                            <Grid>
                                <Text variant="caption" bold>
                                    What is this worker doing well?
                                </Text>
                                <Controller
                                    control={control}
                                    name="going_well"
                                    render={({ field }) => (
                                        <ReviewOptionSelect
                                            value={field.value ?? []}
                                            onChange={field.onChange}
                                            options={positiveOptions}
                                            isScorePositive={true}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid>
                                <Text variant="caption" bold>
                                    What can this worker improve on?
                                </Text>
                                <Controller
                                    control={control}
                                    name="needs_improvement"
                                    render={({ field }) => (
                                        <ReviewOptionSelect
                                            value={field.value ?? []}
                                            onChange={field.onChange}
                                            options={negativeOptions}
                                            isScorePositive={false}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid>
                                <Text bold>
                                    Which of the following can this worker do confidently and independently? (Select all
                                    that apply)
                                    <Controller
                                        control={control}
                                        name="options"
                                        render={({ field }) => (
                                            <ReviewOptionSelect
                                                value={field.value ?? []}
                                                onChange={field.onChange}
                                                options={otherOptions}
                                                isScorePositive={false}
                                                isScoreNeutral={true}
                                            />
                                        )}
                                    />
                                </Text>
                            </Grid>
                        </>
                    )}
                    <Grid>
                        <Text bold>Additional feedback</Text>
                        <Controller
                            control={control}
                            name="text"
                            render={({ field }) => (
                                <FeedbackText value={field.value} onChange={field.onChange} maxRows={8} />
                            )}
                        />
                    </Grid>
                </Grid>
            )}
        </Grid>
    );
}

const FeedbackText = styled(TextArea)({
    width: '100%',
});
